/** Campos C.P. / población / provincia con autocompletado INE (España). */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function tituloMunicipio(nombre) {
  const s = (nombre || "").trim();
  if (!s.includes(",")) return s;
  const [base, articulo] = s.split(",", 2).map((p) => p.trim());
  if (["la", "el", "los", "las"].includes((articulo || "").toLowerCase())) {
    return `${articulo} ${base}`.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return s;
}

export default function DireccionEspanaFields({
  value,
  onChange,
  className = "",
  disabled = false,
}) {
  const form = value || {};
  const [sugerencias, setSugerencias] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [municipiosCp, setMunicipiosCp] = useState([]);
  const [mostrarSug, setMostrarSug] = useState(false);
  const timer = useRef(null);
  const blurTimer = useRef(null);

  useEffect(() => {
    api.get("/codigos-postales/provincias")
      .then((r) => setProvincias(r.data.items || []))
      .catch(() => {});
  }, []);

  const patch = (partial) => onChange?.({ ...form, ...partial });

  const resolverCp = async (cpRaw) => {
    const cp = String(cpRaw || "").replace(/\D/g, "").slice(0, 5);
    patch({ codigo_postal: cp });
    if (cp.length !== 5) {
      setMunicipiosCp([]);
      return;
    }
    try {
      const { data } = await api.get(`/codigos-postales/${cp}`, {
        params: {
          poblacion: form.poblacion || undefined,
          provincia: form.provincia || undefined,
        },
      });
      const munis = data.municipios || [];
      setMunicipiosCp(munis);
      if (munis.length <= 1) {
        patch({
          codigo_postal: data.codigo_postal || cp,
          poblacion: data.poblacion || form.poblacion,
          provincia: data.provincia || form.provincia,
        });
      } else if (!form.poblacion) {
        patch({
          codigo_postal: data.codigo_postal || cp,
          poblacion: data.poblacion || "",
          provincia: data.provincia || "",
        });
      } else {
        patch({
          codigo_postal: data.codigo_postal || cp,
          provincia: data.provincia || form.provincia,
          poblacion: data.poblacion || form.poblacion,
        });
      }
    } catch (e) {
      setMunicipiosCp([]);
      if (e?.response?.status !== 404) toast.error(formatApiError(e));
    }
  };

  const buscarPoblacion = (texto) => {
    patch({ poblacion: texto });
    if (timer.current) clearTimeout(timer.current);
    if ((texto || "").trim().length < 2) {
      setSugerencias([]);
      setMostrarSug(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/codigos-postales/buscar", {
          params: { q: texto.trim(), limit: 12 },
        });
        setSugerencias(data.items || []);
        setMostrarSug(true);
      } catch {
        setSugerencias([]);
      }
    }, 220);
  };

  const elegirSugerencia = async (item) => {
    const poblacion = item.poblacion || tituloMunicipio(item.municipio_nombre);
    const provincia = item.provincia || item.provincia_nombre || "";
    let cp = item.codigo_postal || form.codigo_postal || "";
    try {
      if (item.municipio_id) {
        const { data } = await api.get(`/codigos-postales/municipio/${item.municipio_id}/cps`);
        const cps = data.items || [];
        if (cps.length === 1) cp = cps[0];
        else if (cps.length > 1 && !cps.includes(cp)) cp = cps[0];
      }
    } catch {
      /* keep current cp */
    }
    patch({
      poblacion,
      provincia,
      codigo_postal: cp,
    });
    setSugerencias([]);
    setMostrarSug(false);
    setMunicipiosCp([]);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label>C.P.</Label>
          <Input
            inputMode="numeric"
            maxLength={5}
            disabled={disabled}
            value={form.codigo_postal || ""}
            placeholder="28001"
            onChange={(e) => resolverCp(e.target.value)}
            data-testid="direccion-cp-input"
          />
        </div>
        <div className="relative col-span-2">
          <Label>Población</Label>
          <Input
            disabled={disabled}
            value={form.poblacion || ""}
            placeholder="Escribe para buscar…"
            autoComplete="off"
            onChange={(e) => buscarPoblacion(e.target.value)}
            onFocus={() => sugerencias.length > 0 && setMostrarSug(true)}
            onBlur={() => {
              blurTimer.current = setTimeout(() => setMostrarSug(false), 180);
            }}
            data-testid="direccion-poblacion-input"
          />
          {mostrarSug && sugerencias.length > 0 && (
            <ul className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-zinc-200 bg-white py-1 shadow-lg">
              {sugerencias.map((s) => (
                <li key={`${s.municipio_id}-${s.codigo_postal}`}>
                  <button
                    type="button"
                    className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-zinc-50"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (blurTimer.current) clearTimeout(blurTimer.current);
                      elegirSugerencia(s);
                    }}
                  >
                    <span className="font-medium text-zinc-900">
                      {s.poblacion || tituloMunicipio(s.municipio_nombre)}
                    </span>
                    <span className="text-[11px] text-zinc-500">
                      {s.provincia || s.provincia_nombre}
                      {s.codigo_postal ? ` · CP ${s.codigo_postal}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {municipiosCp.length > 1 && (
        <div>
          <Label>Municipio del C.P. {form.codigo_postal}</Label>
          <Select
            value={form.poblacion || "none"}
            onValueChange={(v) => {
              if (v === "none") return;
              const m = municipiosCp.find(
                (x) => tituloMunicipio(x.municipio_nombre) === v || x.municipio_nombre === v
              );
              patch({
                poblacion: tituloMunicipio(m?.municipio_nombre || v),
                provincia: m?.provincia_nombre || form.provincia,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecciona municipio" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Seleccionar…</SelectItem>
              {municipiosCp.map((m) => {
                const label = tituloMunicipio(m.municipio_nombre);
                return (
                  <SelectItem key={m.municipio_id} value={label}>
                    {label} ({m.provincia_nombre})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>Provincia</Label>
        <Select
          value={form.provincia || "none"}
          disabled={disabled}
          onValueChange={(v) => patch({ provincia: v === "none" ? "" : v })}
        >
          <SelectTrigger data-testid="direccion-provincia-select">
            <SelectValue placeholder="Se rellena con C.P. o población" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Seleccionar…</SelectItem>
            {provincias.map((p) => (
              <SelectItem key={p.codigo} value={p.nombre}>
                {p.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="mt-1 text-[11px] text-zinc-500">
          Datos oficiales INE: al introducir el C.P. o elegir la población se completa la provincia.
        </p>
      </div>
    </div>
  );
}
